import { useEffect, useRef } from 'react';
import { BlockGroup, WorkflowBlock as WorkflowBlockType, PortSide } from '@/types/workflow';
import { cn } from '@/lib/utils';
import { Layers, FolderOpen } from 'lucide-react';
import { dispatchOnboardingEvent } from '@/components/onboarding/uiAdapter';

// 50 % bredare än ett vanligt block (200×80) — användarens spec:
// "Collapsed layers kan vara 50 % större än vanlig block". Höjden
// växer obegränsat med antal chips i 2-kol-griden; ResizeObserver
// rapporterar faktiskt mått tillbaka via onResize så connector-anchors
// hittar rätt kant.
const COLLAPSED_CARD_WIDTH = 300;
const COLLAPSED_CARD_HEIGHT_MIN = 110;
const CHILD_CHIP_MAX_NAME_CHARS = 14;

const DEFAULT_LAYER_ACCENT = '#4f46e5';

interface CollapsedGroupCardProps {
  group: BlockGroup;
  /** Hela canvas-blocks-arrayen — vi filtrerar via group.blockIds för chips. */
  blocks: WorkflowBlockType[];
  /** Hela groups-arrayen — används för att hitta direkt-barn (sub-collapsed-
   *  groups + sub-grupper) så hierarkin kan renderas i rätt prio. */
  groups: BlockGroup[];
  x: number;
  y: number;
  isSelected: boolean;
  onSelect: () => void;
  onDragStart: (e: React.MouseEvent) => void;
  onDoubleClick: () => void;
  onPortClick: (groupId: string, port: PortSide) => void;
  /** Dubbelklick på en sub-collapsed-chip → navigera in i det layret.
   *  Optional eftersom view-only/Preview inte ska kunna navigera. */
  onNavigateIntoChild?: (groupId: string) => void;
  /** Reportera mått så connector-anchors kan hitta rätt kant — kortet
   *  växer med antalet chips så ett statiskt mått skulle drifta. */
  onResize?: (groupId: string, width: number, height: number) => void;
}

interface DirectChildren {
  childCollapsed: BlockGroup[];
  childOpen: BlockGroup[];
  childBlocks: WorkflowBlockType[];
}

/**
 * Direktbarn av `group` i prio-ordning:
 *   1) Sub-collapsed-groups (layers)
 *   2) Sub-grupper (öppna)
 *   3) Block som INTE redan ligger i någon sub-grupp ovan
 *
 * Cyklisk parent-relation guard:as via en visited-set så en korrupt
 * data-payload (g.parentGroupId === g.id eller cyklisk kedja) inte kraschar.
 */
function pickDirectChildren(
  group: BlockGroup,
  allGroups: BlockGroup[],
  allBlocks: WorkflowBlockType[],
): DirectChildren {
  const directGroups = allGroups.filter(
    (g) => g.id !== group.id && g.parentGroupId === group.id,
  );
  const childCollapsed = directGroups.filter((g) => g.collapsed);
  const childOpen = directGroups.filter((g) => !g.collapsed);

  // Block som tillhör group.blockIds men INTE en direkt sub-grupps blockIds.
  const claimedByChild = new Set<string>();
  for (const cg of directGroups) {
    for (const bid of cg.blockIds) claimedByChild.add(bid);
  }
  const childBlocks = group.blockIds
    .filter((bid) => !claimedByChild.has(bid))
    .map((bid) => allBlocks.find((b) => b.id === bid))
    .filter((b): b is WorkflowBlockType => !!b);

  return { childCollapsed, childOpen, childBlocks };
}

/** Singular/plural-suffix per kategori. */
function pluralize(n: number, singular: string, plural: string): string {
  return `${n} ${n === 1 ? singular : plural}`;
}

/**
 * Bygg badge-summary i prio-ordning. Tomma kategorier filtreras bort så
 * en grupp med bara block visar "3 blocks", inte "0 layers · 0 groups · 3 blocks".
 */
function buildSummary(c: DirectChildren): string {
  const parts: string[] = [];
  if (c.childCollapsed.length > 0) parts.push(pluralize(c.childCollapsed.length, 'layer', 'layers'));
  if (c.childOpen.length > 0) parts.push(pluralize(c.childOpen.length, 'group', 'groups'));
  if (c.childBlocks.length > 0) parts.push(pluralize(c.childBlocks.length, 'block', 'blocks'));
  return parts.length > 0 ? parts.join(' · ') : '0 blocks';
}

export function CollapsedGroupCard({
  group,
  blocks,
  groups,
  x,
  y,
  isSelected,
  onSelect,
  onDragStart,
  onDoubleClick,
  onPortClick,
  onNavigateIntoChild,
  onResize,
}: CollapsedGroupCardProps) {
  const displayName = group.name || 'Group';
  const accent = group.color || DEFAULT_LAYER_ACCENT;

  const children = pickDirectChildren(group, groups, blocks);
  const summary = buildSummary(children);

  // Connector-anchors måste veta kortets faktiska storlek (höjden växer
  // med antalet chips). ResizeObserver matar tillbaka via onResize så
  // blockDimensions-indexen alltid stämmer med renderad layout.
  const cardRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const el = cardRef.current;
    if (!el || !onResize) return;
    const report = () => onResize(group.id, el.offsetWidth, el.offsetHeight);
    report();
    // ResizeObserver saknas i äldre jsdom-versioner under test — observa
    // bara om API:t finns. Initial report ovan räcker för att blockets
    // mått hamnar i blockDimensions oavsett.
    if (typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(report);
    ro.observe(el);
    return () => ro.disconnect();
  }, [group.id, onResize]);

  const handleMouseDown = (e: React.MouseEvent) => {
    e.stopPropagation();
    onSelect();
    dispatchOnboardingEvent({ name: 'layer:click', target: group.id });
    dispatchOnboardingEvent({ name: 'block:click', target: 'block', data: { blockId: group.id, layer: true } });
    onDragStart(e);
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    e.stopPropagation();
    onSelect();
    // Drag-start trigas inte här — canvas-touch.ts committar collapsed-
    // group-drag först vid rörelse > TAP_MOVEMENT_THRESHOLD så long-
    // press kan öppna context-menu på tap-and-hold.
  };

  const handlePortClick = (port: PortSide) => (e: React.MouseEvent) => {
    e.stopPropagation();
    onPortClick(group.id, port);
  };

  // Samma 8-port-tuple och samma posClasses-tabell som WorkflowBlock så
  // ports renderas som hela cirklar (50% utanför kortet på cardinals,
  // 33% utanför på corners). Outer-divens overflow:visible tillåter dem
  // att leak:a — clip:en av rounded corners flyttas till en inner-wrapper.
  const ports: PortSide[] = ['top', 'top-right', 'right', 'bottom-right', 'bottom', 'bottom-left', 'left', 'top-left'];
  const posClasses: Record<PortSide, string> = {
    'top': 'left-1/2 top-0 -translate-x-1/2 -translate-y-1/2',
    'top-right': 'right-0 top-0 translate-x-1/3 -translate-y-1/3',
    'right': 'right-0 top-1/2 translate-x-1/2 -translate-y-1/2',
    'bottom-right': 'right-0 bottom-0 translate-x-1/3 translate-y-1/3',
    'bottom': 'left-1/2 bottom-0 -translate-x-1/2 translate-y-1/2',
    'bottom-left': 'left-0 bottom-0 -translate-x-1/3 translate-y-1/3',
    'left': 'left-0 top-1/2 -translate-x-1/2 -translate-y-1/2',
    'top-left': 'left-0 top-0 -translate-x-1/3 -translate-y-1/3',
  };

  // Färg-helpers för indigo-tinted accent. Hex+alpha-suffix istället för
  // color-mix() för bredare browser-support.
  const accentSoft = `${accent}1a`;
  const accentSofter = `${accent}14`;
  const accentBorder = `${accent}38`;
  const accentRing = `${accent}2e`;
  const accentGlow = `${accent}52`;

  return (
    <div
      ref={cardRef}
      data-group-card-id={group.id}
      data-block-id={group.id}
      data-guide="layer-card"
      data-testid="collapsed-group-card"
      className={cn(
        // overflow:visible så port-cirklarna får leak:a utanför ramen.
        // Border-radius klipps i stället på inner-content-wrappern nedan.
        "absolute bg-card cursor-move select-none transition-shadow duration-150 pointer-events-auto",
        "animate-layer-card-pop",
      )}
      style={{
        left: x,
        top: y,
        width: COLLAPSED_CARD_WIDTH,
        minHeight: COLLAPSED_CARD_HEIGHT_MIN,
        borderRadius: 9,
        border: `1px solid hsl(var(--border))`,
        transform: 'translate(-50%, -50%)',
        boxShadow: isSelected
          ? `0 10px 24px -6px ${accentGlow}, 0 4px 10px -2px rgba(0,0,0,0.10), 0 0 0 1px ${accentRing}`
          : '0 1px 2px rgba(0,0,0,0.04), 0 4px 8px -2px rgba(0,0,0,0.06)',
      }}
      tabIndex={0}
      aria-label={`${displayName} layer, ${summary}. Press Enter to open.`}
      onMouseDown={handleMouseDown}
      onTouchStart={handleTouchStart}
      onDoubleClick={(e) => { e.stopPropagation(); onDoubleClick(); }}
      onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); e.stopPropagation(); onDoubleClick(); } }}
    >
      {ports.map((port) => {
        const isCardinal = ['top', 'right', 'bottom', 'left'].includes(port);
        return (
          <div
            key={port}
            role="button"
            aria-label={`Connect ${port} port`}
            tabIndex={0}
            className={cn(
              "absolute rounded-full border-2 bg-card cursor-crosshair transition-all hover:bg-primary hover:border-primary hover:scale-125 z-10",
              posClasses[port],
              isCardinal ? "w-3.5 h-3.5" : "w-3 h-3",
            )}
            style={{ borderColor: accent }}
            data-port={port}
            data-testid={`port-${port}`}
            title={port}
            onClick={handlePortClick(port)}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onPortClick(group.id, port); } }}
            onMouseDown={(e) => e.stopPropagation()}
          />
        );
      })}

      {/* Inner content wrapper — klipps till rounded corners för gradient-fade
          + chip-grid. Port-cirklarna ligger på outer-divens lager utanför
          denna clip så de förblir hela. */}
      <div className="absolute inset-0 overflow-hidden" style={{ borderRadius: 8 }}>
        {/* Top accent stripe — 2.5px */}
        <div
          aria-hidden="true"
          className="absolute top-0 left-0 right-0 pointer-events-none"
          style={{ height: 2.5, backgroundColor: accent }}
        />

        {/* Row 1: Header med gradient-fade */}
        <div
          className="flex items-center gap-[7px]"
          style={{
            padding: '8px 10px 6px',
            background: `linear-gradient(180deg, ${accentSofter} 0%, transparent 100%)`,
          }}
        >
          {/* Layer icon-tile */}
          <div
            className="flex items-center justify-center flex-shrink-0"
            style={{
              width: 20, height: 20, borderRadius: 5,
              backgroundColor: accentSoft,
              boxShadow: `inset 0 0 0 1px ${accentBorder}`,
            }}
          >
            <Layers style={{ width: 11, height: 11, color: accent }} aria-hidden="true" />
          </div>

          {/* Layer name */}
          <h3
            className="font-semibold text-foreground truncate flex-1 min-w-0"
            style={{ fontSize: 11, lineHeight: 1.15, letterSpacing: '-0.005em' }}
          >
            {displayName}
          </h3>

          {/* Summary pill (layers · groups · blocks) */}
          <span
            className="font-mono font-semibold flex-shrink-0"
            style={{
              fontSize: 8.5,
              color: accent,
              padding: '3px 6px',
              backgroundColor: accentSoft,
              border: `1px solid ${accentBorder}`,
              borderRadius: 999,
            }}
            aria-label={summary}
          >
            {summary}
          </span>
        </div>

        {/* Row 2: 2-kolumners grid med direktbarn i prio-ordning. Inga
            overflow-pills — kortet växer på höjden i stället. */}
        <div
          className="grid grid-cols-2"
          style={{ padding: '0 10px 8px', gap: 4 }}
        >
          {children.childCollapsed.map((g) => (
            <SubLayerChip
              key={`l-${g.id}`}
              group={g}
              accent={accent}
              maxChars={CHILD_CHIP_MAX_NAME_CHARS}
              onNavigate={onNavigateIntoChild}
            />
          ))}
          {children.childOpen.map((g) => (
            <SubGroupChip
              key={`g-${g.id}`}
              group={g}
              maxChars={CHILD_CHIP_MAX_NAME_CHARS}
            />
          ))}
          {children.childBlocks.map((b) => (
            <ChildChip key={`b-${b.id}`} block={b} maxChars={CHILD_CHIP_MAX_NAME_CHARS} />
          ))}
        </div>
      </div>
    </div>
  );
}

interface ChildChipProps {
  block: WorkflowBlockType;
  maxChars: number;
}

function ChildChip({ block, maxChars }: ChildChipProps) {
  const name = block.config?.name?.trim() || block.id;
  const truncated = name.length > maxChars ? `${name.slice(0, maxChars - 1)}…` : name;
  const dotColor = block.color || '#94a3b8';
  const dotHalo = `${dotColor}33`;

  return (
    <span
      className="inline-flex items-center font-medium text-foreground min-w-0"
      style={{
        gap: 4,
        padding: '3px 6px 3px 5px',
        fontSize: 8.5,
        backgroundColor: 'hsl(var(--card))',
        border: `1px solid hsl(var(--border))`,
        borderRadius: 999,
      }}
      title={name}
    >
      <span
        aria-hidden="true"
        className="inline-block flex-shrink-0"
        style={{
          width: 6, height: 6, borderRadius: '50%',
          backgroundColor: dotColor,
          boxShadow: `0 0 0 1.5px ${dotHalo}`,
        }}
      />
      <span className="truncate">{truncated}</span>
    </span>
  );
}

interface SubLayerChipProps {
  group: BlockGroup;
  accent: string;
  maxChars: number;
  onNavigate?: (groupId: string) => void;
}

/** Sub-collapsed-group i prio-1. Dubbelklick navigerar in i det layret. */
function SubLayerChip({ group, accent, maxChars, onNavigate }: SubLayerChipProps) {
  const name = group.name?.trim() || 'Layer';
  const truncated = name.length > maxChars ? `${name.slice(0, maxChars - 1)}…` : name;
  const tint = `${(group.color || accent)}1a`;
  const tintBorder = `${(group.color || accent)}38`;

  return (
    <span
      className="inline-flex items-center font-medium text-foreground min-w-0 cursor-pointer"
      style={{
        gap: 4,
        padding: '3px 6px 3px 5px',
        fontSize: 8.5,
        backgroundColor: tint,
        border: `1px solid ${tintBorder}`,
        borderRadius: 999,
      }}
      title={`Layer: ${name} (double-click to open)`}
      onMouseDown={(e) => e.stopPropagation()}
      onDoubleClick={(e) => {
        if (!onNavigate) return;
        e.stopPropagation();
        onNavigate(group.id);
      }}
    >
      <Layers
        aria-hidden="true"
        style={{ width: 8, height: 8, color: group.color || accent }}
      />
      <span className="truncate">{truncated}</span>
    </span>
  );
}

interface SubGroupChipProps {
  group: BlockGroup;
  maxChars: number;
}

/** Sub-grupp (ej collapsed) i prio-2. Visuellt åtskild från layer-chip
 *  via folder-ikon + neutral bakgrund. */
function SubGroupChip({ group, maxChars }: SubGroupChipProps) {
  const name = group.name?.trim() || 'Group';
  const truncated = name.length > maxChars ? `${name.slice(0, maxChars - 1)}…` : name;
  const dotColor = group.color || '#94a3b8';

  return (
    <span
      className="inline-flex items-center font-medium text-foreground min-w-0"
      style={{
        gap: 4,
        padding: '3px 6px 3px 5px',
        fontSize: 8.5,
        backgroundColor: 'hsl(var(--card))',
        border: `1px dashed hsl(var(--border))`,
        borderRadius: 999,
      }}
      title={`Group: ${name}`}
    >
      <FolderOpen
        aria-hidden="true"
        style={{ width: 8, height: 8, color: dotColor }}
      />
      <span className="truncate">{truncated}</span>
    </span>
  );
}

export { COLLAPSED_CARD_WIDTH, COLLAPSED_CARD_HEIGHT_MIN };
